import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Select from "react-select";
import { Modal, OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast } from "react-toastify";
import PageTitle from "../../layouts/PageTitle";
import { fetchClients } from "../../../services/clientApi";
import { fetchProjects } from "../../../services/projectApi";
import { fetchVendors } from "../../../services/vendorApi";
import { fetchUsers } from "../../../services/userApi";
import { fetchBusinessOrdersForProject, fileUrl } from "../../../services/assignVendorApi";
import { exportPaymentLogs, getPaymentLogs } from "../../../services/paymentLogApi";
import {
  ACTION_LABELS,
  STAGE_LABELS,
  actionBadgeClass,
  actorLabel,
  formatChangeValue,
  formatDate,
  formatDateTime,
  soLabel,
  statusLabel,
} from "./paymentLogUtils";
import "../AssignVendor/AssignVendorList.css";

/** Same compact react-select look as the Assigned vendors filters. */
const FILTER_SELECT_STYLES = {
  container: (base) => ({ ...base, width: "100%" }),
  control: (base, state) => ({
    ...base,
    width: "100%",
    minHeight: 28,
    borderColor: state.isFocused ? "#2563eb" : "#ced4da",
    boxShadow: state.isFocused ? "0 0 0 0.12rem rgba(37,99,235,.15)" : "none",
    "&:hover": { borderColor: "#94a3b8" },
    fontSize: "0.75rem",
  }),
  valueContainer: (base) => ({ ...base, padding: "0 6px", fontSize: "0.75rem" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "0.75rem" }),
  dropdownIndicator: (base) => ({ ...base, padding: "0 4px" }),
  clearIndicator: (base) => ({ ...base, padding: "0 4px" }),
  placeholder: (base) => ({ ...base, color: "#6c757d", fontSize: "0.75rem" }),
  singleValue: (base) => ({ ...base, color: "#212529", fontSize: "0.75rem" }),
  multiValueLabel: (base) => ({ ...base, fontSize: "0.7rem" }),
  menu: (base) => ({ ...base, zIndex: 9999, fontSize: "0.75rem" }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
  option: (base) => ({ ...base, fontSize: "0.75rem", padding: "4px 8px" }),
};

const PAGE_SIZES = [10, 25, 50];

const EMPTY_FILTERS = {
  clientId: "",
  projectId: "",
  businessOrderId: "",
  vendorId: "",
  stage: [],
  action: [],
  actorId: "",
  from: "",
  to: "",
  search: "",
};

const REJECTION_ACTIONS = ["ADMIN_REJECTED", "HOD_REJECTED", "FINANCE_REJECTED"];

function normalizeId(v) {
  if (v == null || v === "") return "";
  if (typeof v === "object") return v._id != null ? String(v._id) : "";
  return String(v).trim();
}

function toYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Local-day bounds → ISO so "today" means the viewer's day, not UTC's. */
function dayStartIso(ymd) {
  return ymd ? new Date(`${ymd}T00:00:00`).toISOString() : "";
}
function dayEndIso(ymd) {
  return ymd ? new Date(`${ymd}T23:59:59.999`).toISOString() : "";
}

function sameList(a, b) {
  return a.length === b.length && a.every((x) => b.includes(x));
}

const QUICK_CHIPS = [
  {
    key: "paid",
    label: "Payments approved",
    isActive: (f) => sameList(f.action, ["PAYMENT_APPROVED_PAID"]),
    apply: (f, on) => ({ ...f, action: on ? ["PAYMENT_APPROVED_PAID"] : [] }),
  },
  {
    key: "rejections",
    label: "Rejections",
    isActive: (f) => sameList(f.action, REJECTION_ACTIONS),
    apply: (f, on) => ({ ...f, action: on ? REJECTION_ACTIONS : [] }),
  },
  {
    key: "today",
    label: "Today",
    isActive: (f) => {
      const t = toYmd(new Date());
      return f.from === t && f.to === t;
    },
    apply: (f, on) => {
      const t = toYmd(new Date());
      return { ...f, from: on ? t : "", to: on ? t : "" };
    },
  },
  {
    key: "week",
    label: "This week",
    isActive: (f) => {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      return f.from === toYmd(monday) && f.to === toYmd(now);
    },
    apply: (f, on) => {
      const now = new Date();
      const monday = new Date(now);
      monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
      return { ...f, from: on ? toYmd(monday) : "", to: on ? toYmd(now) : "" };
    },
  },
];

function errorMessage(err, fallback) {
  return err?.response?.data?.message || err?.message || fallback;
}

/** Blob error bodies (export) carry the JSON message as text. */
async function blobErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const json = JSON.parse(await data.text());
      return json?.message || fallback;
    } catch {
      return fallback;
    }
  }
  return errorMessage(err, fallback);
}

const selectPortal = typeof document !== "undefined" ? document.body : null;

const PaymentLogList = () => {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [searchInput, setSearchInput] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [users, setUsers] = useState([]);
  const [boOptions, setBoOptions] = useState([]);

  const loadGen = useRef(0);

  useEffect(() => {
    Promise.all([
      fetchClients().catch(() => []),
      fetchProjects().catch(() => []),
      fetchVendors().catch(() => []),
      fetchUsers().catch(() => []),
    ]).then(([c, p, v, u]) => {
      setClients(Array.isArray(c) ? c : []);
      setProjects(Array.isArray(p) ? p : []);
      setVendors(Array.isArray(v) ? v : []);
      setUsers(Array.isArray(u) ? u : []);
    });
  }, []);

  useEffect(() => {
    if (!filters.projectId) {
      setBoOptions([]);
      return;
    }
    fetchBusinessOrdersForProject(filters.projectId)
      .then((list) => setBoOptions(Array.isArray(list) ? list : []))
      .catch((err) => {
        setBoOptions([]);
        toast.error(errorMessage(err, "Could not load sales orders."));
      });
  }, [filters.projectId]);

  // Debounce free-text search into the filters.
  useEffect(() => {
    const t = setTimeout(() => {
      setFilters((f) => (f.search === searchInput.trim() ? f : { ...f, search: searchInput.trim() }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  const queryParams = useMemo(
    () => ({
      clientId: filters.clientId,
      projectId: filters.projectId,
      businessOrderId: filters.businessOrderId,
      vendorId: filters.vendorId,
      stage: filters.stage,
      action: filters.action,
      actorId: filters.actorId,
      from: dayStartIso(filters.from),
      to: dayEndIso(filters.to),
      search: filters.search,
    }),
    [filters],
  );

  const load = useCallback(() => {
    const gen = ++loadGen.current;
    setLoading(true);
    getPaymentLogs({ ...queryParams, page, limit })
      .then((data) => {
        if (gen !== loadGen.current) return;
        setItems(data.items);
        setTotal(data.total);
      })
      .catch((err) => {
        if (gen !== loadGen.current) return;
        toast.error(errorMessage(err, "Could not load payment log."));
      })
      .finally(() => {
        if (gen === loadGen.current) setLoading(false);
      });
  }, [queryParams, page, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const updateFilters = (patch) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  };

  const clearAll = () => {
    setFilters(EMPTY_FILTERS);
    setSearchInput("");
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportPaymentLogs(queryParams);
    } catch (err) {
      toast.error(await blobErrorMessage(err, "Export failed."));
    } finally {
      setExporting(false);
    }
  };

  const clientOptions = useMemo(
    () => clients.map((c) => ({ value: normalizeId(c._id), label: c.clientName || "—" })),
    [clients],
  );
  const projectOptions = useMemo(
    () =>
      projects
        .filter((p) => filters.clientId && normalizeId(p.clientId) === filters.clientId)
        .map((p) => ({ value: normalizeId(p._id), label: p.projectName || "—" })),
    [projects, filters.clientId],
  );
  const soOptions = useMemo(
    () => boOptions.map((o) => ({ value: normalizeId(o.id), label: o.label || "—" })),
    [boOptions],
  );
  const vendorOptions = useMemo(
    () => vendors.map((v) => ({ value: normalizeId(v._id), label: v.vendorName || "—" })),
    [vendors],
  );
  const userOptions = useMemo(
    () =>
      users.map((u) => ({
        value: normalizeId(u._id),
        label: u.userName || u.email || normalizeId(u._id),
      })),
    [users],
  );
  const stageOptions = useMemo(
    () => Object.entries(STAGE_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );
  const actionOptions = useMemo(
    () => Object.entries(ACTION_LABELS).map(([value, label]) => ({ value, label })),
    [],
  );

  const pick = (opts, v) => opts.find((o) => o.value === v) ?? null;
  const pickMany = (opts, vs) => opts.filter((o) => vs.includes(o.value));

  const hasFilters =
    JSON.stringify({ ...filters, search: "" }) !== JSON.stringify({ ...EMPTY_FILTERS }) ||
    Boolean(searchInput.trim());

  const totalPages = Math.max(1, Math.ceil(total / limit));
  const firstRow = total === 0 ? 0 : (page - 1) * limit + 1;
  const lastRow = Math.min(total, page * limit);

  return (
    <Fragment>
      <PageTitle activeMenu="Payment log" motherMenu="Vendor" pageContent="Payment log" />

      <div className="row">
        <div className="col-12 mb-3">
          <div className="card border shadow-sm">
            <div className="py-3 px-3">
              <div className="d-flex flex-wrap gap-2 mb-3">
                {QUICK_CHIPS.map((chip) => {
                  const active = chip.isActive(filters);
                  return (
                    <button
                      key={chip.key}
                      type="button"
                      className={`btn btn-sm rounded-pill ${active ? "btn-primary" : "btn-outline-primary"}`}
                      onClick={() => {
                        setFilters((f) => chip.apply(f, !active));
                        setPage(1);
                      }}
                    >
                      {chip.label}
                    </button>
                  );
                })}
              </div>

              <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                <button
                  type="button"
                  className="btn btn-link btn-sm p-0 text-decoration-none fw-semibold"
                  onClick={() => setDrawerOpen((v) => !v)}
                  aria-expanded={drawerOpen}
                >
                  <i className={`fa fa-chevron-${drawerOpen ? "down" : "right"} me-1`} aria-hidden />
                  Filters
                </button>
                <button
                  type="button"
                  className="btn btn-outline-secondary btn-sm"
                  onClick={clearAll}
                  disabled={!hasFilters}
                >
                  Clear all
                </button>
              </div>

              {drawerOpen ? (
                <div className="row row-cols-1 row-cols-sm-2 row-cols-xl-5 g-2 align-items-end w-100 mx-0">
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Client
                    </label>
                    <Select
                      instanceId="pl-filter-client"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isClearable
                      placeholder="Search or select…"
                      options={clientOptions}
                      value={pick(clientOptions, filters.clientId)}
                      onChange={(opt) =>
                        updateFilters({ clientId: opt?.value ?? "", projectId: "", businessOrderId: "" })
                      }
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Project
                    </label>
                    <Select
                      instanceId="pl-filter-project"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isClearable
                      isDisabled={!filters.clientId}
                      placeholder={filters.clientId ? "Search or select…" : "Select client first…"}
                      options={projectOptions}
                      value={pick(projectOptions, filters.projectId)}
                      onChange={(opt) => updateFilters({ projectId: opt?.value ?? "", businessOrderId: "" })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      SO (Sales order)
                    </label>
                    <Select
                      instanceId="pl-filter-so"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isClearable
                      isDisabled={!filters.projectId}
                      placeholder={filters.projectId ? "Search or select…" : "Select project first…"}
                      options={soOptions}
                      value={pick(soOptions, filters.businessOrderId)}
                      onChange={(opt) => updateFilters({ businessOrderId: opt?.value ?? "" })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Vendor
                    </label>
                    <Select
                      instanceId="pl-filter-vendor"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isClearable
                      placeholder="Search or select…"
                      options={vendorOptions}
                      value={pick(vendorOptions, filters.vendorId)}
                      onChange={(opt) => updateFilters({ vendorId: opt?.value ?? "" })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Performed by
                    </label>
                    <Select
                      instanceId="pl-filter-actor"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isClearable
                      placeholder="Search or select…"
                      options={userOptions}
                      value={pick(userOptions, filters.actorId)}
                      onChange={(opt) => updateFilters({ actorId: opt?.value ?? "" })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Stage
                    </label>
                    <Select
                      instanceId="pl-filter-stage"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isMulti
                      placeholder="Any stage"
                      options={stageOptions}
                      value={pickMany(stageOptions, filters.stage)}
                      onChange={(opts) => updateFilters({ stage: (opts || []).map((o) => o.value) })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Action
                    </label>
                    <Select
                      instanceId="pl-filter-action"
                      styles={FILTER_SELECT_STYLES}
                      menuPortalTarget={selectPortal}
                      menuPosition="fixed"
                      isMulti
                      placeholder="Any action"
                      options={actionOptions}
                      value={pickMany(actionOptions, filters.action)}
                      onChange={(opts) => updateFilters({ action: (opts || []).map((o) => o.value) })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      From
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm w-100"
                      style={{ fontSize: "0.75rem", minHeight: 28 }}
                      value={filters.from}
                      onChange={(e) => updateFilters({ from: e.target.value })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      To
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm w-100"
                      style={{ fontSize: "0.75rem", minHeight: 28 }}
                      value={filters.to}
                      onChange={(e) => updateFilters({ to: e.target.value })}
                    />
                  </div>
                  <div className="col min-w-0">
                    <label className="form-label text-muted mb-0 small" style={{ fontSize: 11 }}>
                      Search
                    </label>
                    <input
                      type="search"
                      className="form-control form-control-sm w-100"
                      style={{ fontSize: "0.75rem", minHeight: 28 }}
                      placeholder="Payment no., name, reason, reference…"
                      value={searchInput}
                      onChange={(e) => {
                        setSearchInput(e.target.value);
                        setPage(1);
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>

        <div className="col-12">
          <div className="card">
            <div className="card-header py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-2">
              <h6 className="mb-0 fw-semibold">Payment log</h6>
              <button
                type="button"
                className="btn btn-success btn-sm"
                onClick={handleExport}
                disabled={exporting || total === 0}
              >
                <i className="fa fa-file-excel-o me-1" aria-hidden />
                {exporting ? "Exporting…" : "Export Excel"}
              </button>
            </div>
            <div className="card-body p-0">
              <div className="table-responsive">
                <table className="table table-sm table-hover mb-0 small align-middle">
                  <thead className="table-light">
                    <tr>
                      <th>Date &amp; time</th>
                      <th>Payment No.</th>
                      <th>SO No</th>
                      <th>Vendor</th>
                      <th>Client</th>
                      <th>Project</th>
                      <th>Action</th>
                      <th>From → To</th>
                      <th>Performed by</th>
                      <th>Reason</th>
                      <th>Payment date / ref</th>
                      <th>Attachments</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan={12} className="text-center py-4">
                          <div className="spinner-border spinner-border-sm text-primary" role="status" />
                        </td>
                      </tr>
                    ) : items.length === 0 ? (
                      <tr>
                        <td colSpan={12} className="text-center text-muted py-4">
                          No payment log entries match these filters.
                        </td>
                      </tr>
                    ) : (
                      items.map((log) => (
                        <tr key={log._id} style={{ cursor: "pointer" }} onClick={() => setSelected(log)}>
                          <td className="text-nowrap">{formatDateTime(log.createdAt)}</td>
                          <td className="text-nowrap">{log.paymentNumber || "—"}</td>
                          <td>{soLabel(log.businessOrderId)}</td>
                          <td>{log.vendorId?.vendorName || "—"}</td>
                          <td>{log.clientId?.clientName || "—"}</td>
                          <td>{log.projectId?.projectName || "—"}</td>
                          <td>
                            <span className={`badge ${actionBadgeClass(log.action, log.toStatus)}`}>
                              {ACTION_LABELS[log.action] || log.action}
                            </span>
                          </td>
                          <td className="text-nowrap">
                            {log.fromStatus || log.toStatus
                              ? `${statusLabel(log.fromStatus)} → ${statusLabel(log.toStatus)}`
                              : "—"}
                          </td>
                          <td>{actorLabel(log)}</td>
                          <td style={{ maxWidth: 220 }}>
                            {log.reason ? (
                              <OverlayTrigger
                                placement="top"
                                overlay={<Tooltip id={`pl-reason-${log._id}`}>{log.reason}</Tooltip>}
                              >
                                <span className="d-inline-block text-truncate" style={{ maxWidth: 220 }}>
                                  {log.reason}
                                </span>
                              </OverlayTrigger>
                            ) : (
                              "—"
                            )}
                          </td>
                          <td className="text-nowrap">
                            {log.paymentDate ? formatDate(log.paymentDate) : "—"}
                            {log.paymentReference ? (
                              <div className="text-muted">{log.paymentReference}</div>
                            ) : null}
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            {Array.isArray(log.attachments) && log.attachments.length
                              ? log.attachments.map((f) => (
                                  <a
                                    key={f.path}
                                    href={fileUrl(f.path)}
                                    download={f.originalName || "file"}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="d-block text-truncate"
                                    style={{ maxWidth: 160 }}
                                    title={f.originalName || "file"}
                                  >
                                    <i className="fa fa-download me-1" aria-hidden />
                                    {f.originalName || "file"}
                                  </a>
                                ))
                              : "—"}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            <div className="card-footer py-2 px-3 d-flex flex-wrap align-items-center justify-content-between gap-2 small">
              <div className="d-flex align-items-center gap-2">
                <span className="text-muted">Rows per page</span>
                <select
                  className="form-select form-select-sm"
                  style={{ width: 80 }}
                  value={limit}
                  onChange={(e) => {
                    setLimit(Number(e.target.value));
                    setPage(1);
                  }}
                >
                  {PAGE_SIZES.map((n) => (
                    <option key={n} value={n}>
                      {n}
                    </option>
                  ))}
                </select>
                <span className="text-muted">
                  {firstRow}–{lastRow} of {total}
                </span>
              </div>
              <div className="btn-group btn-group-sm">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={page <= 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                >
                  Previous
                </button>
                <span className="btn btn-outline-secondary disabled">
                  Page {page} of {totalPages}
                </span>
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  disabled={page >= totalPages || loading}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Modal show={Boolean(selected)} onHide={() => setSelected(null)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title as="h6">
            {selected ? `${ACTION_LABELS[selected.action] || selected.action} · ${selected.paymentNumber || ""}` : ""}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="small">
          {selected ? (
            <>
              <dl className="row mb-3">
                <dt className="col-sm-4">Date &amp; time</dt>
                <dd className="col-sm-8">{formatDateTime(selected.createdAt)}</dd>
                <dt className="col-sm-4">Stage</dt>
                <dd className="col-sm-8">{STAGE_LABELS[selected.stage] || selected.stage}</dd>
                <dt className="col-sm-4">Action</dt>
                <dd className="col-sm-8">
                  <span className={`badge ${actionBadgeClass(selected.action, selected.toStatus)}`}>
                    {ACTION_LABELS[selected.action] || selected.action}
                  </span>
                </dd>
                <dt className="col-sm-4">From → To</dt>
                <dd className="col-sm-8">
                  {statusLabel(selected.fromStatus)} → {statusLabel(selected.toStatus)}
                </dd>
                <dt className="col-sm-4">Payment No.</dt>
                <dd className="col-sm-8">{selected.paymentNumber || "—"}</dd>
                <dt className="col-sm-4">SO No</dt>
                <dd className="col-sm-8">{soLabel(selected.businessOrderId)}</dd>
                <dt className="col-sm-4">Client / Project</dt>
                <dd className="col-sm-8">
                  {selected.clientId?.clientName || "—"} / {selected.projectId?.projectName || "—"}
                </dd>
                <dt className="col-sm-4">Vendor</dt>
                <dd className="col-sm-8">{selected.vendorId?.vendorName || "—"}</dd>
                <dt className="col-sm-4">Performed by</dt>
                <dd className="col-sm-8">
                  {actorLabel(selected)}
                  {selected.actorEmail ? <div className="text-muted">{selected.actorEmail}</div> : null}
                </dd>
                <dt className="col-sm-4">Reason</dt>
                <dd className="col-sm-8" style={{ whiteSpace: "pre-wrap" }}>
                  {selected.reason || "—"}
                </dd>
                <dt className="col-sm-4">Payment date</dt>
                <dd className="col-sm-8">{formatDate(selected.paymentDate)}</dd>
                <dt className="col-sm-4">Payment reference</dt>
                <dd className="col-sm-8">{selected.paymentReference || "—"}</dd>
                <dt className="col-sm-4">Amount to vendor / client</dt>
                <dd className="col-sm-8">
                  {selected.amountSnapshot?.costToAgency || "—"} /{" "}
                  {selected.amountSnapshot?.costToClient || "—"}
                  {selected.amountSnapshot?.vatAmount ? ` (VAT ${selected.amountSnapshot.vatAmount})` : ""}{" "}
                  {selected.amountSnapshot?.currency || ""}
                </dd>
                <dt className="col-sm-4">Attachments</dt>
                <dd className="col-sm-8">
                  {Array.isArray(selected.attachments) && selected.attachments.length
                    ? selected.attachments.map((f) => (
                        <a
                          key={f.path}
                          href={fileUrl(f.path)}
                          download={f.originalName || "file"}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="d-block"
                        >
                          <i className="fa fa-download me-1" aria-hidden />
                          {f.originalName || "file"}
                        </a>
                      ))
                    : "—"}
                </dd>
                <dt className="col-sm-4">Source</dt>
                <dd className="col-sm-8">{selected.source || "—"}</dd>
                <dt className="col-sm-4">IP / User agent</dt>
                <dd className="col-sm-8 text-break">
                  {selected.ipAddress || "—"}
                  {selected.userAgent ? <div className="text-muted">{selected.userAgent}</div> : null}
                </dd>
              </dl>

              <h6 className="fw-semibold" style={{ fontSize: 13 }}>
                Changes
              </h6>
              {Array.isArray(selected.changes) && selected.changes.length ? (
                <table className="table table-sm table-bordered mb-0">
                  <thead className="table-light">
                    <tr>
                      <th>Field</th>
                      <th>Before</th>
                      <th>After</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.changes.map((c, i) => (
                      <tr key={`${c.field}-${i}`}>
                        <td>{c.field}</td>
                        <td className="text-break">{formatChangeValue(c.field, c.from)}</td>
                        <td className="text-break">{formatChangeValue(c.field, c.to)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-muted">No field changes recorded.</div>
              )}
            </>
          ) : null}
        </Modal.Body>
      </Modal>
    </Fragment>
  );
};

export default PaymentLogList;
